import React, { useRef, useState } from 'react';

import useOutsideClick from '@hooks/useOutsideClick';

import Input from '@uikit/Form/Input/Input';
import { Button } from '@uikit/Form/Button/Button';
import { SVGIcon } from '@uikit/old/Icon/Icon';

function CreateItemComponent({
  onCreate,
  entityName,
  onCancel,
  isOpen = false,
  initialText = `New ${entityName}`,
  testId,
}: {
  onCreate: (name?: string) => void;
  entityName: string;
  onCancel?: () => void;
  isOpen?: boolean;
  initialText?: string;
  /** Hook base: the closed button gets `testId`, the open form `${testId}-input` / `${testId}-submit`. */
  testId?: string;
}) {
  const inputRef = useRef();
  const buttonRef = useRef();

  const [isCreating, setIsCreating] = useState<boolean>(isOpen);
  const [name, setName] = useState<string>(initialText);

  // A name of spaces only is no name (RES-7345 #67).
  const trimmedName = name.trim();

  const resetState = () => {
    setIsCreating(false);
    setName(`New ${entityName.toLowerCase()}`);
  };

  const submit = () => {
    if (!trimmedName) {
      return;
    }
    onCreate(trimmedName);
    // The check button closes the form like Enter does, so a second click cannot create a duplicate.
    resetState();
  };

  const handleInputKeyDown = (e) => {
    if (e.keyCode === 27 || (e.keyCode === 13 && !trimmedName)) {
      onCancel && onCancel();
      resetState();
    } else if (e.keyCode === 13) {
      submit();
    }
  };

  useOutsideClick([inputRef, buttonRef], () => {
    onCancel && onCancel();
    resetState();
  });

  if (isCreating) {
    return (
      <div className="folders__create-btn">
        <Input
          inputRef={inputRef}
          className="folders__create-input"
          value={name}
          onChange={({ target: { value } }) => setName(value)}
          onKeyDown={handleInputKeyDown}
          // onBlur={resetState}
          maxLength={25}
          placeholder={`${entityName} name`}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <Button
          innerRef={buttonRef}
          className="folders__create-input-btn"
          onClick={submit}
          disabled={!trimmedName}
          data-testid={testId ? `${testId}-submit` : undefined}
        >
          <SVGIcon icon="check" size={14} />
        </Button>
      </div>
    );
  }

  return (
    <div
      className="folders__create-btn folders__create-btn--inactive"
      onClick={() => setIsCreating(true)}
      data-testid={testId}
    >
      + Create {entityName}
    </div>
  );
}

export default CreateItemComponent;
